// INSTRUCTIONS - Outside of subaccounts and allocations, do not use .validate, .setTouched, or .setIsRequired, .hasError from any fields
// - All validation outside of subaccounts and allocations is handled by Yup validation schema

import { createSelector } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import type { DealBookingType } from '@src/types/OTC';
import {
  isFixedRateLoanDealForm,
  isFixedRateLoanDealFormDraft,
  isFloatingRateLoanDealForm,
  isFloatingRateLoanDealFormDraft,
  isForwardDealForm,
  isForwardDealFormDraft,
  isOptionDealForm,
  isOptionDealFormDraft,
  isRepoDealForm,
  isRepoDealFormDraft,
} from '@src/types/OTC/otcGuards';
import { resolveSecuredLoanCollateralAsset } from '@src/utils/OTC/loanSecurity';
import type { PayoffTypeEnum } from '@talos/kyoko';
import {
  AllocationValueTypeEnum,
  Field,
  LoanQuoteTerminationTypeEnum,
  NumericField,
  Unit,
  getTypedKeys,
  isFixedRateLoanProductTemplate,
  isFloatingRateLoanProductTemplate,
  isOTCForwardProductTemplate,
  type DateOnly,
  type QtyInputCurrencyOptions,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { dealSubAccountFieldName, getInitialFormState } from './manualDealProductLogic';
import { createManualDealSymbolGuards } from './manualDealSymbolSource';
import type { ManualDealTouchState, ManualDealTradeState } from './types';
import { allocationTotalValidation } from './validation/FieldRules';

export { getInitialFormState } from './manualDealProductLogic';

// Helper function to convert Allocation[] to field format
export function mapAllocationsToField(
  allocations: { subAccount: string; value: string }[],
  valueType: AllocationValueTypeEnum = AllocationValueTypeEnum.Percentage,
  isUserEntry = false,
  bookingType?: DealBookingType
): { subAccountField: Field<string>; valueField: NumericField }[] {
  return allocations.map(alloc => ({
    subAccountField: new Field({
      name: dealSubAccountFieldName(bookingType),
      value: alloc.subAccount ? alloc.subAccount : undefined,
      isTouched: isUserEntry,
    }),
    valueField: new NumericField({
      name: 'Allocation amount',
      unit: valueType === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal,
    }).updateValue(alloc.value || undefined, !isUserEntry),
  }));
}

// Type for notional calculation parameters
type NotionalCalculationParams = {
  notionalValue: string | null | undefined;
  notionalValueWithCurrency: { value: string; currency: string } | null | undefined;
  payoffType: PayoffTypeEnum | undefined;
};

// Helper function to handle notional amount calculation updates
export function updateNotionalAmountFromCalculation(
  state: WritableDraft<ManualDealTradeState>,
  params: NotionalCalculationParams
): void {
  const { notionalValue, notionalValueWithCurrency, payoffType } = params;

  // Handle Forward forms: only set value for Linear and Inverse payoff types
  if (isForwardDealFormDraft(state.form)) {
    const shouldSetValue = notionalValue && (payoffType === 'Linear' || payoffType === 'Inverse');

    // Update notional amount field
    state.form.notionalAmountField = state.form.notionalAmountField.updateValue(
      shouldSetValue ? notionalValue : undefined
    );

    // Update notional with currency fields
    state.form.notionalWithCurrencyField.value = state.form.notionalWithCurrencyField.value.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.value : undefined
    );
    state.form.notionalWithCurrencyField.currency = state.form.notionalWithCurrencyField.currency.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.currency : undefined
    );
    return;
  }

  // Handle Option forms: always set value if present (Options use Linear logic)
  if (isOptionDealFormDraft(state.form)) {
    const shouldSetValue = !!notionalValue;

    // Update notional amount field
    state.form.notionalAmountField = state.form.notionalAmountField.updateValue(
      shouldSetValue ? notionalValue : undefined
    );

    // Update notional with currency fields
    state.form.notionalWithCurrencyField.value = state.form.notionalWithCurrencyField.value.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.value : undefined
    );
    state.form.notionalWithCurrencyField.currency = state.form.notionalWithCurrencyField.currency.updateValue(
      shouldSetValue && notionalValueWithCurrency ? notionalValueWithCurrency.currency : undefined
    );
    return;
  }
}

export function getInitialTouchState(): ManualDealTouchState {
  return {
    bookingType: false,
    side: false,
    templateSymbol: false,
    symbol: false,
    market: false,
    marketAccount: false,
    comments: false,
    counterparty: false,
    quantity: false,
    tradeDate: false,
    effectiveTime: false,
    settlementTime: false,
    expiryTime: false,

    // Forward-specific fields
    quantityCurrency: false,
    unitNotional: false,
    notionalAmount: false,
    forwardPrice: false,
    index: false,
    spread: false,
    commission: false,
    commissionCurrency: false,
    initialMarginCurrency: false,
    haircutPercent: false,
    initialMarginPercent: false,
    releaseMarginPercent: false,
    maintenanceMarginPercent: false,
    marginCallPercent: false,
    commissionCalculationType: false,
    liquidationMarginPercent: false,

    // Loan-specific fields
    loanSide: false,
    rate: false,
    rateSource: false,
    loanOriginationFeeSpread: false,
    loanOriginationFee: false,
    thresholdsMinimum: false,
    thresholdsMarginCall: false,
    thresholdsLiquidation: false,
    thresholdsCollateralRelease: false,
    initialCollateralAmount: false,
    initialCollateralPercent: false,
    collateralSubAccount: false,

    // Option-specific fields
    strikePrice: false,
    optionType: false,
    premium: false,

    // Repo-specific fields
    direction: false,
    primeBroker: false,
    haircutPct: false,
    initialMarginPct: false,
    marginReleasePct: false,
    maintenanceMarginPct: false,
    marginCallPct: false,
    closeOutMarginPct: false,
  };
}

export function getInitialState(): ManualDealTradeState {
  return {
    form: getInitialFormState('forward'),
    touchState: getInitialTouchState(),
    referenceData: initialRefDataState,
    additionalDependencies: { tradableSubAccounts: [] },
    validationErrors: {}, // Initialize empty validation errors
    requiredFields: {},
    initialized: false,
    isLoading: false,
    isSubmitting: false,
    dealBeingModified: undefined,
    computedSettlementDate: undefined,
    computedExpiryDate: undefined,
  };
}

export type CommonDateFieldsPartial = Partial<{
  tradeDate: DateOnly | undefined;
  effectiveTime: DateOnly | undefined;
  settlementTime: string | undefined;
  expiryTime: string | undefined;
}>;

export function applyCommonDateFields(
  state: WritableDraft<ManualDealTradeState>,
  updates: CommonDateFieldsPartial
): void {
  if ('tradeDate' in updates) {
    state.form.tradeDateField = state.form.tradeDateField.updateValue(updates.tradeDate ?? undefined);
  }
  if ('effectiveTime' in updates) {
    state.form.effectiveDateField = state.form.effectiveDateField.updateValue(updates.effectiveTime ?? undefined);
  }
  if ('settlementTime' in updates) {
    state.form.settlementDateField = state.form.settlementDateField.updateValue(updates.settlementTime);
    if (updates.settlementTime != null) {
      state.computedSettlementDate = undefined;
    }
  }
  if ('expiryTime' in updates) {
    state.form.expiryDateField = state.form.expiryDateField.updateValue(updates.expiryTime);
    if (updates.expiryTime != null) {
      state.computedExpiryDate = undefined;
    }
  }
}

export function validateAllocations(state: WritableDraft<ManualDealTradeState>): void {
  const isSubAccountRequired = state.additionalDependencies.tradableSubAccounts.length > 0;

  state.form.allocations = state.form.allocations.map(pair => ({
    subAccountField: pair.subAccountField.setIsRequired(isSubAccountRequired).validate(),
    valueField: pair.valueField.validate(isSubAccountRequired ? [allocationTotalValidation] : [], state.form),
  }));
}

export function handleSymbolChange(
  state: WritableDraft<ManualDealTradeState>,
  symbol?: string,
  isModify = false
): void {
  if (!symbol) {
    // Spurious clears (e.g. async option refresh) must not wipe modify hydration
    if (state.dealBeingModified) {
      return;
    }
    state.form.symbolField = state.form.symbolField.updateValue(undefined);
    return;
  }

  const security = state.referenceData.securities?.securitiesBySymbol.get(symbol);
  if (!security) {
    return;
  }

  const symbolGuards = createManualDealSymbolGuards(isModify);

  if (isForwardDealFormDraft(state.form) && symbolGuards.isForward(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    // Only set unitNotional from security if not modifying a deal (let deal data take precedence)
    if (!isModify && isOTCForwardProductTemplate(security)) {
      const productDetails = security.ProductDetails;
      state.form.unitNotionalField = state.form.unitNotionalField.updateValue(productDetails.UnitNotionalAmount);
    }
    return;
  }

  if (
    (isFixedRateLoanDealFormDraft(state.form) || isFloatingRateLoanDealFormDraft(state.form)) &&
    (symbolGuards.isFixedRateLoan(security) || symbolGuards.isFloatingRateLoan(security))
  ) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    return;
  }

  if (isOptionDealFormDraft(state.form) && symbolGuards.isOption(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    return;
  }

  if (isRepoDealFormDraft(state.form) && symbolGuards.isRepo(security)) {
    state.form.symbolField = state.form.symbolField.updateValue(security);
    return;
  }
}

export function clearRepoSettlementDate(state: WritableDraft<ManualDealTradeState>): void {
  if (!isRepoDealFormDraft(state.form)) {
    return;
  }

  state.form.settlementDateField = state.form.settlementDateField.updateValue(undefined);
  state.computedSettlementDate = undefined;
}

export function clearRepoExpiryDate(state: WritableDraft<ManualDealTradeState>): void {
  if (!isRepoDealFormDraft(state.form)) {
    return;
  }

  state.form.expiryDateField = state.form.expiryDateField.updateValue(undefined);
  state.computedExpiryDate = undefined;
}

export function handleTemplateSymbolChange(
  state: WritableDraft<ManualDealTradeState>,
  symbol?: string,
  isModify = false
): void {
  const templateChanged = symbol !== state.form.templateSymbolField.value?.Symbol;

  if (!symbol) {
    // updateCommon does not pass isModify; during Modify Deal, SearchSelect can fire undefined when
    // otcProductTemplates refreshes — do not clear hydrated template/symbol.
    if (state.dealBeingModified) {
      return;
    }
    state.form.templateSymbolField = state.form.templateSymbolField.updateValue(undefined);
    // For new deals, also clear the symbol field when template is cleared
    if (!isModify) {
      state.form.symbolField = state.form.symbolField.updateValue(undefined);
    }
    if (!isModify && templateChanged) {
      clearRepoExpiryDate(state);
      clearRepoSettlementDate(state);
    }
    return;
  }

  const security = state.referenceData.securities?.securitiesBySymbol.get(symbol);
  if (security) {
    state.form.templateSymbolField = state.form.templateSymbolField.updateValue(security);

    // For new deals, set the symbol field to the same value as template
    if (!isModify) {
      state.form.symbolField = state.form.symbolField.updateValue(security);
    }

    // Only set unitNotional from security if not modifying a deal (let deal data take precedence)
    if (isForwardDealFormDraft(state.form) && isOTCForwardProductTemplate(security) && !isModify) {
      const productDetails = security.ProductDetails;
      state.form.unitNotionalField = state.form.unitNotionalField.updateValue(productDetails.UnitNotionalAmount);
    }

    // Set RateSource from ProductDetails for floating rate loans
    if (isFloatingRateLoanDealFormDraft(state.form) && isFloatingRateLoanProductTemplate(security) && !isModify) {
      const productDetails = security.ProductDetails;
      if (productDetails?.RateSource) {
        state.form.rateSourceField = state.form.rateSourceField.updateValue(productDetails.RateSource);
      }
    }

    // Open-term loans have no maturity or settlement at trade entry — clear stale dates when switching templates.
    if (
      !isModify &&
      (isFixedRateLoanDealFormDraft(state.form) || isFloatingRateLoanDealFormDraft(state.form)) &&
      (isFixedRateLoanProductTemplate(security) || isFloatingRateLoanProductTemplate(security))
    ) {
      if (security.ProductDetails?.TermType === LoanQuoteTerminationTypeEnum.OpenTerm) {
        state.form.expiryDateField = state.form.expiryDateField.updateValue(undefined);
        state.computedExpiryDate = undefined;
        state.form.settlementDateField = state.form.settlementDateField.updateValue(undefined);
        state.computedSettlementDate = undefined;
      }

      // Clear stale collateral sub-account when switching to an unsecured template
      if (
        !resolveSecuredLoanCollateralAsset(
          security.ProductDetails?.LoanSecurityType,
          security.ProductDetails?.CollateralAsset
        )
      ) {
        state.form.collateralSubAccountField = state.form.collateralSubAccountField.updateValue(undefined);
      }
    }

    if (!isModify && templateChanged) {
      clearRepoExpiryDate(state);
      clearRepoSettlementDate(state);
    }
  }
}

export function handleMarketAccountChange(
  state: WritableDraft<ManualDealTradeState>,
  marketAccount: string | undefined
): void {
  const repoMarketAccountChanged =
    isRepoDealFormDraft(state.form) && marketAccount !== state.form.marketAccountField.value;

  state.form.marketAccountField = state.form.marketAccountField.updateValue(marketAccount);

  if (repoMarketAccountChanged) {
    clearRepoSettlementDate(state);
  }
}

export function setTouchedValueForAllDealFields(state: WritableDraft<ManualDealTradeState>, touched: boolean): void {
  // Set all form fields as touched
  getTypedKeys(state.form).forEach(key => {
    const field = state.form[key];
    if (field && typeof field === 'object' && 'setTouched' in field) {
      field.setTouched(touched);
    }
  });
}

export const selectManualDealUseTradeAllocations = createSelector(
  (state: AppState) => state.referenceData.settings.useTradeAllocations,
  (state: AppState) => state.manualDeal.dealBeingModified,
  (state: AppState) => state.manualDeal.form.bookingTypeField.value,
  (useTradeAllocationsSetting, dealBeingModified, bookingType) => {
    // Modify: preserve split UI only when the deal already has Allocation
    if (dealBeingModified) {
      return dealBeingModified.deal.Allocation != null;
    }

    // PORT-19296: pin loan/repo create to a single Sub Account until split-allocation implications are understood
    if (bookingType === 'fixedRateLoan' || bookingType === 'floatingRateLoan' || bookingType === 'repo') {
      return false;
    }

    // Forward/option create: honor the org trading setting (treat unset as enabled)
    return useTradeAllocationsSetting !== false;
  }
);

/**
 * Selector for the quantity currency options for a forward deal (based on BaseAsset, QuoteAsset, or Units)
 */
export const selectForwardDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isForwardDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || !symbolGuards.isForward(security)) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BaseAsset, QuoteAsset } = productDetails;
    const options: QtyInputCurrencyOptions<string>[] = [];

    if (BaseAsset) {
      options.push({ currency: BaseAsset, label: BaseAsset });
    }
    if (QuoteAsset) {
      options.push({ currency: QuoteAsset, label: QuoteAsset });
    }

    // Always include Units option for forward deals
    options.push({ currency: 'Units', label: 'Units' });

    return options;
  }
);

/**
 * Selector for the quantity currency options for a loan deal (based on BorrowedAsset or Units)
 * Applies to both Fixed Rate Loans and Floating Rate Loans
 */
export const selectLoanDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isFixedRateLoanDealForm(form) && !isFloatingRateLoanDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || (!symbolGuards.isFixedRateLoan(security) && !symbolGuards.isFloatingRateLoan(security))) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BorrowedAsset } = productDetails;
    const options: QtyInputCurrencyOptions<string>[] = [];

    if (BorrowedAsset) {
      options.push({ currency: BorrowedAsset, label: BorrowedAsset });
    }

    options.push({ currency: 'Units', label: 'Units' });

    return options;
  }
);

/**
 * Selector for the quantity currency options for an option deal (based on BaseAsset, QuoteAsset, or Units)
 */
export const selectOptionDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isOptionDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || !symbolGuards.isOption(security)) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { BaseAsset, QuoteAsset } = productDetails;
    const optionsByCurrency = new Map<string, QtyInputCurrencyOptions<string>>();

    if (BaseAsset) {
      optionsByCurrency.set(BaseAsset, { currency: BaseAsset, label: BaseAsset });
    }
    if (QuoteAsset) {
      optionsByCurrency.set(QuoteAsset, { currency: QuoteAsset, label: QuoteAsset });
    }

    // Always include Units option for option deals
    optionsByCurrency.set('Units', { currency: 'Units', label: 'Units' });
    return Array.from(optionsByCurrency.values());
  }
);

/**
 * Selector for the quantity currency options for a repo deal (based on RepoAsset or Units)
 */
export const selectRepoDealQuantityCurrencyOptions = createSelector(
  [(state: AppState) => state.manualDeal.form, (state: AppState) => state.manualDeal.dealBeingModified],
  (form, dealBeingModified) => {
    if (!isRepoDealForm(form)) {
      return [];
    }

    const symbolGuards = createManualDealSymbolGuards(dealBeingModified != null);
    const security = symbolGuards.securityForMode(form);
    if (!security || !symbolGuards.isRepo(security)) {
      return [];
    }

    const productDetails = security.ProductDetails;
    if (!productDetails) {
      return [];
    }

    const { RepoAsset } = productDetails;
    const options: QtyInputCurrencyOptions<string>[] = [];

    if (RepoAsset) {
      options.push({ currency: RepoAsset, label: RepoAsset });
    }

    options.push({ currency: 'Units', label: 'Units' });

    return options;
  }
);
